import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import PageContainer from '@/common/components/PageContainer'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { useTablePagination } from '@/common/hooks/useTablePaginate'
import { useQueryState, parseAsString } from 'nuqs'
import { Button } from '@/common/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Badge } from '@/common/ui/badge'
import { ArrowLeft, Trophy, Users, PlayCircle } from 'lucide-react'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/common/ui/alert-dialog'
import useQueryGetTournamentById from '@/modules/tournament/api/hooks/useQueryGetTournamentById'
import useQueryGetTournamentPlayers from '@/modules/tournament/api/hooks/useQueryGetTournamentPlayers'
import useMutationFinalizeTournament from '@/modules/tournament/api/hooks/useMutationFinalizeTournament'
import TournamentPlayersTable from '@/modules/tournament/components/TournamentPlayersTable'
import { TOURNAMENT_STATUS_BADGE_MAP } from '@/modules/tournament/constants'
import type { paths } from '@/schema'

type TournamentPlayerStatus =
	paths['/api/admin/tournaments/{id}/players']['get']['parameters']['query']['status']

function TournamentPlayersPage() {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const [showFinalizeDialog, setShowFinalizeDialog] = useState(false)

	const { pageSize, currentPage, searchQuery } = useTablePagination()

	const [status] = useQueryState(
		'status',
		parseAsString.withOptions({ shallow: false }).withDefault(''),
	)

	const query = {
		take: pageSize,
		skip: (currentPage - 1) * pageSize,
		search: searchQuery || undefined,
		status: (status as TournamentPlayerStatus) || undefined,
	}

	const { data: tournament, isLoading: isTournamentLoading } =
		useQueryGetTournamentById(id || '')
	const { data: playersData, isLoading: isPlayersLoading } =
		useQueryGetTournamentPlayers(id || '', query)

	const finalizeMutation = useMutationFinalizeTournament(id || '')

	if (isTournamentLoading) {
		return (
			<PageContainer>
				<Card>
					<CardContent className="p-6">
						<div className="flex h-32 items-center justify-center">
							<div className="text-muted-foreground">กำลังโหลด...</div>
						</div>
					</CardContent>
				</Card>
			</PageContainer>
		)
	}

	if (!tournament) {
		return (
			<PageContainer>
				<Card>
					<CardContent className="p-6">
						<div className="flex h-32 items-center justify-center">
							<div className="text-destructive">ไม่พบทัวร์นาเมนต์</div>
						</div>
					</CardContent>
				</Card>
			</PageContainer>
		)
	}

	const statusVariant =
		TOURNAMENT_STATUS_BADGE_MAP[tournament.status] || 'default'
	const statusLabel =
		tournament.status === 'DRAFT'
			? 'ฉบับร่าง'
			: tournament.status === 'ACTIVE'
				? 'กำลังดำเนินการ'
				: tournament.status === 'ENDED'
					? 'สิ้นสุดแล้ว'
					: 'ยกเลิก'

	const canFinalize = tournament.status === 'ENDED'

	return (
		<PageContainer>
			<div className="space-y-6">
				<div className="flex items-center gap-4">
					<Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
						<ArrowLeft className="h-4 w-4" />
					</Button>
					<div className="flex-1">
						<h2 className="text-2xl font-semibold">{tournament.name}</h2>
						<p className="text-sm text-muted-foreground">
							{tournament.description}
						</p>
					</div>
					<Badge variant={statusVariant}>{statusLabel}</Badge>
				</div>

				{tournament.bannerImage && (
					<Card>
						<CardContent className="p-4">
							<img
								src={tournament.bannerImage}
								alt={tournament.name}
								className="h-auto w-full rounded-lg object-cover"
								style={{ maxHeight: '280px' }}
							/>
						</CardContent>
					</Card>
				)}

				<div className="grid gap-4 md:grid-cols-3">
					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">
								จำนวนผู้เข้าร่วม
							</CardTitle>
							<Users className="h-4 w-4 text-muted-foreground" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold">
								{tournament.playerCount || 0} คน
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">
								ช่วงเวลาการแข่งขัน
							</CardTitle>
							<PlayCircle className="h-4 w-4 text-muted-foreground" />
						</CardHeader>
						<CardContent>
							<div className="text-sm">
								<div>
									{format(new Date(tournament.startDate), 'dd MMM yyyy', {
										locale: th,
									})}
								</div>
								<div className="text-muted-foreground">ถึง</div>
								<div>
									{format(new Date(tournament.endDate), 'dd MMM yyyy', {
										locale: th,
									})}
								</div>
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">
								เงินรางวัลรวม
							</CardTitle>
							<Trophy className="h-4 w-4 text-muted-foreground" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold">
								฿
								{tournament.prizes
									.reduce((sum, prize) => sum + Number(prize.prizeAmount), 0)
									.toLocaleString()}
							</div>
						</CardContent>
					</Card>
				</div>

				{(tournament.allowedProviders?.length ||
					tournament.allowedGames?.length) && (
					<Card>
						<CardHeader>
							<CardTitle className="text-sm">ข้อจำกัดเกมและค่าย</CardTitle>
						</CardHeader>
						<CardContent className="space-y-3">
							{tournament.allowedProviders &&
								tournament.allowedProviders.length > 0 && (
									<div>
										<p className="mb-2 text-xs text-muted-foreground">
											ค่ายที่อนุญาต:
										</p>
										<div className="flex flex-wrap gap-2">
											{tournament.allowedProviders.map((provider) => (
												<Badge key={provider.id} variant="outline">
													{provider.displayName || provider.name}
												</Badge>
											))}
										</div>
									</div>
								)}
							{tournament.allowedGames &&
								tournament.allowedGames.length > 0 && (
									<div>
										<p className="mb-2 text-xs text-muted-foreground">
											เกมที่อนุญาต:
										</p>
										<div className="flex flex-wrap gap-2">
											{tournament.allowedGames.map((game) => (
												<Badge key={game.id} variant="secondary">
													{game.displayName || game.name}
												</Badge>
											))}
										</div>
									</div>
								)}
						</CardContent>
					</Card>
				)}

				<Card>
					<CardHeader>
						<div className="flex items-center justify-between">
							<CardTitle>ผู้เข้าร่วมทัวร์นาเมนต์</CardTitle>
							{canFinalize && (
								<Button
									variant="default"
									onClick={() => setShowFinalizeDialog(true)}
								>
									<Trophy className="mr-2 h-4 w-4" />
									ประกาศผลและแจกรางวัล
								</Button>
							)}
						</div>
					</CardHeader>
					<CardContent>
						<TournamentPlayersTable
							data={playersData?.data || []}
							total={playersData?.total || 0}
							isLoading={isPlayersLoading}
						/>
					</CardContent>
				</Card>
			</div>

			<AlertDialog
				open={showFinalizeDialog}
				onOpenChange={setShowFinalizeDialog}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>ยืนยันการประกาศผลและแจกรางวัล</AlertDialogTitle>
						<AlertDialogDescription>
							คุณแน่ใจหรือไม่ที่จะประกาศผลและแจกรางวัล?
							การดำเนินการนี้จะแจกรางวัลเข้ากระเป๋าเงินของผู้ชนะทันที
							และไม่สามารถย้อนกลับได้
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>ยกเลิก</AlertDialogCancel>
						<AlertDialogAction
							onClick={async () => {
								await finalizeMutation.mutateAsync()
								setShowFinalizeDialog(false)
							}}
						>
							ยืนยัน
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</PageContainer>
	)
}

export default withUserPermission(TournamentPlayersPage, {
	permissionType: PermissionType.Read,
	permissionKey: [PermissionKeys.MANAGE_GAME],
})
